import React, { useState, useEffect, useMemo } from "react";
import "./../comparativeAnalysis.less";
import materialsDB from '@/constants/materialsDB';
import { rebuildFormula } from '@/config/func';

interface StreamData {
  name: string;
  T_K: number;
  T_C: number;
  P_Pa: number;
  P_bar: number;
  P_KPa: number;
  P_MPa: number;
  P_atm: number;
  molar_frac: number[];
  mass_frac: number[];
  f_mol_per_h: number;
  f_kg_per_h: number;
  f_nm3_per_h: number;
  f_m3_per_h: number;
}

interface TaskStreamData {
  taskId: string;
  taskName: string;
  Dresult: StreamData | null;
  Bresult: StreamData | null;
}

interface DataType {
  温度: Array<{
    项目: string;
    单位: string;
    数据: Array<{ taskName: string; D: number; B: number }>;
  }>;
  压力: Array<{
    项目: string;
    单位: string;
    数据: Array<{ taskName: string; D: number; B: number }>;
  }>;
  质量浓度: any[];
  摩尔流量: any[];
  质量流量: any[];
  摩尔分率: any[];
}

interface CalculateTask3DYCZTableProps {
  inputData: any;
  multiResultsData: any[][];
  taskList: Array<{ taskId: string; taskName: string }>;
  streamsData: any;
  transInputs?: any[]; // 每个任务对应的 input 数据
}

const T_ITEMS = [
  { label: "K", value: "T_K" },
  { label: "℃", value: "T_C" },
];

const P_ITEMS = [
  { label: "Pa", value: "P_Pa" },
  { label: "bar", value: "P_bar" },
  { label: "KPa", value: "P_KPa" },
  { label: "MPa", value: "P_MPa" },
  { label: "atm", value: "P_atm" },
];

const safeToFixed = (value: any, digits: number = 2): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return "0";
  }
  return Number(value).toFixed(digits);
};

export default function CalculateTask3DYCZTable({
  inputData,
  multiResultsData,
  taskList,
  streamsData,
  transInputs,
}: CalculateTask3DYCZTableProps) {
  const [selectT, setSelectT] = useState("T_K");
  const [selectP, setSelectP] = useState("P_Pa");
  const [showTModal, setShowTModal] = useState(false);
  const [showPModal, setShowPModal] = useState(false);
  const [selectButton, setSelectButton] = useState("摩尔分率");
  const [data, setData] = useState<DataType | null>(null);
  const [numDigit, setNumDigit] = useState(2);

  const findMaterial = (item: any) => {
    const ma = materialsDB.find((m: any) => m.cas === item.cas);
    if (ma) {
      return `${rebuildFormula(ma.Molecular_formula)} （${ma.Name_ZH}）`;
    } else {
      return rebuildFormula(item.name);
    }
  };

  const analyInputData = useMemo(() => {
    if (!inputData || !multiResultsData || !streamsData || !taskList.length) return null;
    if (inputData.length !== 1) return null;

    let Dname = "";
    let Bname = "";
    for (let i = 0; i < inputData[0].outputStreams.length; i++) {
      if (inputData[0].outputStreams[i].rule === "D") Dname = inputData[0].outputStreams[i].name;
      if (inputData[0].outputStreams[i].rule === "B") Bname = inputData[0].outputStreams[i].name;
    }
    if (!Dname || !Bname) return null;

    const taskStreamList: TaskStreamData[] = taskList.map((task, index) => {
      const taskStreams = multiResultsData[index];
      return {
        taskId: task.taskId,
        taskName: task.taskName,
        Dresult: taskStreams?.find((s: any) => s.name === Dname) || null,
        Bresult: taskStreams?.find((s: any) => s.name === Bname) || null,
      };
    });

    const firstValid = taskStreamList.find(t => t.Dresult && t.Bresult);
    if (!firstValid) return null;
    const matLen = streamsData.materials.length;
    if (
      matLen !== firstValid.Dresult?.molar_frac.length ||
      matLen !== firstValid.Bresult?.molar_frac.length
    ) {
      return null;
    }

    return {
      温度: [
        {
          项目: "温度",
          单位: T_ITEMS.find(t => t.value === selectT)?.label || "K",
          数据: taskStreamList.map(t => ({
            taskName: t.taskName,
            D: t.Dresult?.[selectT] || 0,
            B: t.Bresult?.[selectT] || 0,
          })),
        },
      ],
      压力: [
        {
          项目: "压力",
          单位: P_ITEMS.find(p => p.value === selectP)?.label || "Pa",
          数据: taskStreamList.map(t => ({
            taskName: t.taskName,
            D: t.Dresult?.[selectP] || 0,
            B: t.Bresult?.[selectP] || 0,
          })),
        },
      ],
      质量浓度: taskStreamList.map((task, tIndex) => ({
        "项目/流股": `${task.taskName}`,
        单位: "mg/Nm³",
        data: streamsData.materials.map((m: any, i: number) => ({
          D: task.Dresult ? (task.Dresult.mass_frac[i] * task.Dresult.f_kg_per_h * 1e6) / (task.Dresult.f_nm3_per_h || 1) : 0,
          B: task.Bresult ? (task.Bresult.mass_frac[i] * task.Bresult.f_kg_per_h * 1e6) / (task.Bresult.f_m3_per_h || 1) : 0,
        })),
      })),
      摩尔流量: taskStreamList.map((task, tIndex) => ({
        "项目/流股": `${task.taskName}`,
        单位: "mol/h",
        data: streamsData.materials.map((m: any, i: number) => ({
          D: task.Dresult ? task.Dresult.molar_frac[i] * task.Dresult.f_mol_per_h : 0,
          B: task.Bresult ? task.Bresult.molar_frac[i] * task.Bresult.f_mol_per_h : 0,
        })),
      })),
      质量流量: taskStreamList.map((task, tIndex) => ({
        "项目/流股": `${task.taskName}`,
        单位: "kg/h",
        data: streamsData.materials.map((m: any, i: number) => ({
          D: task.Dresult ? task.Dresult.mass_frac[i] * task.Dresult.f_kg_per_h : 0,
          B: task.Bresult ? task.Bresult.mass_frac[i] * task.Bresult.f_kg_per_h : 0,
        })),
      })),
      摩尔分率: taskStreamList.map((task, tIndex) => ({
        "项目/流股": `${task.taskName}`,
        单位: "-",
        data: streamsData.materials.map((m: any, i: number) => ({
          D: task.Dresult ? task.Dresult.molar_frac[i] : 0,
          B: task.Bresult ? task.Bresult.molar_frac[i] : 0,
        })),
      })),
    };
  }, [inputData, multiResultsData, taskList, streamsData, selectT, selectP]);

  useEffect(() => {
    setData(analyInputData);
  }, [analyInputData]);

  const formatValue = (value: any) => {
    return safeToFixed(value, numDigit);
  };

  // 温度/压力表格行渲染
  const renderPropertyTableRows = () => {
    if (!data) return null;
    const rows: React.ReactNode[] = [];

    taskList.forEach((task, tIndex) => {
      const tempData = data.温度[0].数据[tIndex];
      const pressData = data.压力[0].数据[tIndex];

      rows.push(
        <div key={`${tIndex}-D`} className="table-data-row">
          <div className="table-cell data-cell">{`${task.taskName}-塔顶气相`}</div>
          {/* <div className="table-cell data-cell">-</div> */}
          <div className="table-cell data-cell">{formatValue(tempData.D)}</div>
          <div className="table-cell data-cell">{formatValue(pressData.D)}</div>
        </div>
      );

      rows.push(
        <div key={`${tIndex}-B`} className="table-data-row">
          <div className="table-cell data-cell">{`${task.taskName}-塔底液相`}</div>
          {/* <div className="table-cell data-cell">-</div> */}
          <div className="table-cell data-cell">{formatValue(tempData.B)}</div>
          <div className="table-cell data-cell">{formatValue(pressData.B)}</div>
        </div>
      );
    });

    return rows;
  };

  // 组分表格表头
  const renderMaterialTableHeader = () => {
    const base = ["项目/流股", "单位"];
    const materialCols = streamsData?.materials?.map((m: any) => findMaterial(m)) || [];
    return [...base, ...materialCols];
  };

  // 组分表格行渲染
  const renderMaterialTableRows = () => {
    if (!data) return null;
    const rows: React.ReactNode[] = [];

    taskList.forEach((task, tIndex) => {
      const item = data[selectButton][tIndex];
      if (!item) return;

      rows.push(
        <div key={`${tIndex}-D`} className="material-data-row">
          <div className="material-cell">{`${task.taskName}-塔顶气相`}</div>
          <div className="material-cell">{item.单位 || "-"}</div>
          {item.data.map((matData: any, matIndex: number) => (
            <div key={matIndex} className="material-cell">{formatValue(matData.D)}</div>
          ))}
        </div>
      );

      rows.push(
        <div key={`${tIndex}-B`} className="material-data-row">
          <div className="material-cell">{`${task.taskName}-塔底液相`}</div>
          <div className="material-cell">{item.单位 || "-"}</div>
          {item.data.map((matData: any, matIndex: number) => (
            <div key={matIndex} className="material-cell">{formatValue(matData.B)}</div>
          ))}
        </div>
      );
    });

    return rows;
  };

  if (!data) return <div className="data-empty">暂无计算数据</div>;

  return (
    <div className="calculate-task-table-container">
      {/* 温度压力表格 */}
      
<div className="co-table-scroll-wrapper">
  <div className="core-table-wrapper">
    <div className="table-header-row">
      <div className="table-cell header-cell">项目/流股</div>
      {/* <div className="table-cell header-cell">单位</div> */}

      {/* 温度 + 单位选择器 */}
      <div className="table-cell header-cell">
        温度
        <div className="unit-selector" onClick={(e) => {
          e.stopPropagation();
          setShowTModal(!showTModal);
        }}>
          <span className="unit-text">{data.温度[0].单位}</span>
          <span className="unit-icon">▼</span>
          {showTModal && (
            <div className="unit-dropdown t-dropdown">
              {T_ITEMS.map(item => (
                <div key={item.value} className="dropdown-item" onClick={() => {
                  setSelectT(item.value);
                  setShowTModal(false);
                }}>{item.label}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 压力 + 单位选择器 */}
      <div className="table-cell header-cell">
        压力
        <div className="unit-selector" onClick={(e) => {
          e.stopPropagation();
          setShowPModal(!showPModal);
        }}>
          <span className="unit-text">{data.压力[0].单位}</span>
          <span className="unit-icon">▼</span>
          {showPModal && (
            <div className="unit-dropdown p-dropdown">
              {P_ITEMS.map(item => (
                <div key={item.value} className="dropdown-item" onClick={() => {
                  setSelectP(item.value);
                  setShowPModal(false);
                }}>{item.label}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>

    {renderPropertyTableRows()}
  </div>
</div>

      <div className="data-type-buttons">
        {["摩尔分率", "质量浓度", "摩尔流量", "质量流量"].map(type => (
          <button
            key={type}
            className={`type-button ${selectButton === type ? "active" : ""}`}
            onClick={() => setSelectButton(type)}
          >
            {type}
          </button>
        ))}
      </div>

      {/* 组分表格 */}
      <div className="co-table-scroll-wrapper">
        <div className="co-material-table-wrapper">
          <div className="material-table-header">
            {renderMaterialTableHeader().map((h, i) => (
              <div key={i} className="material-header-cell">{h}</div>
            ))}
          </div>

          <div className="material-table-body">
            {renderMaterialTableRows()}
          </div>
        </div>
      </div>
    </div>
  );
}